import { useEffect, useState } from "react";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/StatusBadge";
import { Download, CheckCircle2, Wallet, Pencil, X, Save } from "lucide-react";
import { toast } from "sonner";

export default function Payments() {
  const { user, refresh } = useAuth();
  const [pays, setPays] = useState([]);
  const [me, setMe] = useState(null);

  const load = async () => {
    const { data } = await api.get("/payments");
    setPays(data || []);
  };
  const loadMe = async () => {
    const { data } = await api.get("/auth/me");
    setMe(data);
  };
  useEffect(() => {
    load();
    if (user.role === "freelancer") loadMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const approve = async (id) => {
    try { await api.post(`/payments/${id}/approve`); toast.success("Approved"); load(); } catch { toast.error("Failed"); }
  };
  const markPaid = async (id) => {
    try { await api.post(`/payments/${id}/mark-paid`); toast.success("Marked paid"); load(); } catch { toast.error("Failed"); }
  };

  const totals = pays.reduce((acc, p) => {
    acc.total += p.amount;
    if (p.status === "Paid") acc.paid += p.amount;
    else acc.pending += p.amount;
    return acc;
  }, { total: 0, paid: 0, pending: 0 });

  const downloadCsv = async () => {
    try {
      const res = await api.get("/payments/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "payments.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  return (
    <AppLayout title={user.role === "freelancer" ? "Earnings" : "Payments"} subtitle="Financial overview">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-5">
        <div className="kpi-card"><div className="text-[11px] uppercase text-slate-500">Total</div><div className="font-display text-2xl sm:text-3xl font-bold text-slate-900 mt-1">${totals.total.toFixed(2)}</div></div>
        <div className="kpi-card"><div className="text-[11px] uppercase text-slate-500">Paid</div><div className="font-display text-2xl sm:text-3xl font-bold text-emerald-600 mt-1">${totals.paid.toFixed(2)}</div></div>
        <div className="kpi-card"><div className="text-[11px] uppercase text-slate-500">Pending</div><div className="font-display text-2xl sm:text-3xl font-bold text-amber-600 mt-1">${totals.pending.toFixed(2)}</div></div>
      </div>

      {user.role === "freelancer" && me && (
        <EarningsPayoutsCard u={me} onSaved={() => { loadMe(); refresh(); }} />
      )}

      {(user.role === "admin" || user.role === "manager") && (
        <div className="flex justify-end mb-3">
          <Button variant="outline" onClick={downloadCsv} data-testid="btn-export-csv"><Download className="w-4 h-4 mr-1.5"/>Export CSV</Button>
        </div>
      )}

      {/* Desktop table */}
      <div className="hidden md:block bg-white border border-slate-200 rounded-md overflow-hidden">
        <table className="data-table">
          <thead><tr>
            {user.role !== "freelancer" && <th>Freelancer</th>}
            <th>Task</th><th>Project</th><th>Amount</th><th>Status</th><th>Created</th>
            {(user.role === "admin" || user.role === "manager") && <th>Action</th>}
          </tr></thead>
          <tbody>
            {pays.map(p => (
              <tr key={p.id} data-testid={`row-payment-${p.id}`}>
                {user.role !== "freelancer" && <td>{p.freelancer_name}</td>}
                <td>{p.task_title}</td>
                <td>{p.project_name}</td>
                <td className="font-mono">${(p.amount||0).toFixed(2)}</td>
                <td><StatusBadge kind="payment" value={p.status}/></td>
                <td className="text-xs text-slate-500">{new Date(p.created_at).toLocaleDateString()}</td>
                {(user.role === "admin" || user.role === "manager") && (
                  <td>
                    {p.status === "Pending" && <Button size="sm" variant="outline" onClick={()=>approve(p.id)} data-testid={`btn-approve-${p.id}`}>Approve</Button>}
                    {p.status === "Approved" && user.role === "admin" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={()=>markPaid(p.id)} data-testid={`btn-mark-paid-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Mark paid</Button>}
                  </td>
                )}
              </tr>
            ))}
            {pays.length === 0 && <tr><td colSpan={user.role !== "freelancer" ? 7 : 5} className="text-center py-8 text-slate-500">No payments yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="payments-mobile-list">
        {pays.length === 0 && <div className="bg-white border border-slate-200 rounded-md p-6 text-center text-slate-500">No payments yet.</div>}
        {pays.map(p => (
          <div key={p.id} className="bg-white border border-slate-200 rounded-md p-4" data-testid={`card-payment-${p.id}`}>
            <div className="flex items-start justify-between gap-3 mb-2">
              <div className="font-display font-semibold text-slate-900 text-sm flex-1 line-clamp-2">{p.task_title}</div>
              <StatusBadge kind="payment" value={p.status}/>
            </div>
            <div className="text-xs text-slate-500 mb-3">{p.project_name}</div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              {user.role !== "freelancer" && (
                <div><div className="text-[10px] uppercase text-slate-400">Freelancer</div><div className="font-medium text-slate-900 truncate">{p.freelancer_name}</div></div>
              )}
              <div><div className="text-[10px] uppercase text-slate-400">Amount</div><div className="font-mono font-semibold text-slate-900">${(p.amount||0).toFixed(2)}</div></div>
              <div className="col-span-2"><div className="text-[10px] uppercase text-slate-400">Created</div><div className="text-xs text-slate-700">{new Date(p.created_at).toLocaleString()}</div></div>
            </div>
            {(user.role === "admin" || user.role === "manager") && (
              <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                {p.status === "Pending" && <Button size="sm" variant="outline" onClick={()=>approve(p.id)} className="flex-1" data-testid={`mbtn-approve-${p.id}`}>Approve</Button>}
                {p.status === "Approved" && user.role === "admin" && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 flex-1" onClick={()=>markPaid(p.id)} data-testid={`mbtn-mark-paid-${p.id}`}><CheckCircle2 className="w-3.5 h-3.5 mr-1"/>Mark paid</Button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </AppLayout>
  );
}

function EarningsPayoutsCard({ u, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const ninetyDaysAgo = new Date(Date.now() - 90 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [start, setStart] = useState(ninetyDaysAgo);
  const [end, setEnd] = useState(today);
  const [downloading, setDownloading] = useState(null);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState({ payment_method: u.payment_method || "PayPal", payment_id: u.payment_id || "" });

  const startEdit = () => {
    setDraft({ payment_method: u.payment_method || "PayPal", payment_id: u.payment_id || "" });
    setEditing(true);
  };
  const cancel = () => setEditing(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.post("/profile/payout", { payment_method: draft.payment_method, payment_id: draft.payment_id });
      toast.success("Payout method saved");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save payout");
    } finally { setBusy(false); }
  };

  const download = async (fmt) => {
    if (!start || !end) { toast.error("Please pick a date range"); return; }
    if (new Date(start) > new Date(end)) { toast.error("'From' date must be before 'To'"); return; }
    setDownloading(fmt);
    try {
      const resp = await api.get(`/earnings/statement?start=${start}&end=${end}&format=${fmt}`, { responseType: "blob" });
      const blob = new Blob([resp.data], { type: fmt === "pdf" ? "application/pdf" : "text/csv" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `earnings_${start}_${end}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement (${fmt.toUpperCase()}) downloaded`);
    } catch (err) {
      let msg = "Could not download statement";
      if (err.response?.data instanceof Blob) {
        try { msg = JSON.parse(await err.response.data.text()).detail || msg; } catch { /* ignore */ }
      } else { msg = err.response?.data?.detail || msg; }
      toast.error(msg);
    } finally { setDownloading(null); }
  };

  return (
    <div className="kpi-card mb-5" data-testid="section-earnings">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-md bg-slate-900 text-cyan-400 flex items-center justify-center"><Wallet className="w-4 h-4" strokeWidth={1.75}/></div>
          <div>
            <h3 className="font-display font-bold text-slate-900">Earnings & Payouts</h3>
            <div className="text-[11px] text-slate-500 uppercase tracking-wider mt-0.5">Payout configuration & statement</div>
          </div>
        </div>
        {!editing ? (
          <Button size="sm" variant="ghost" onClick={startEdit} data-testid="section-earnings-edit"><Pencil className="w-3.5 h-3.5 mr-1"/>Edit payout</Button>
        ) : (
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={cancel} data-testid="section-earnings-cancel"><X className="w-3.5 h-3.5 mr-1"/>Cancel</Button>
            <Button size="sm" onClick={save} disabled={busy} className="bg-cyan-600 hover:bg-cyan-700" data-testid="section-earnings-save"><Save className="w-3.5 h-3.5 mr-1"/>Save</Button>
          </div>
        )}
      </div>

      <div className="space-y-5">
        {/* Payout configuration */}
        <div>
          <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">Payout method</div>
          {!editing ? (
            <div className="grid grid-cols-2 gap-3 text-sm" data-testid="payout-view">
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider">Method</div>
                <div className="font-medium text-slate-900 mt-0.5">{u.payment_method || <span className="text-sm text-slate-500 italic">Not set</span>}</div>
              </div>
              <div>
                <div className="text-[11px] uppercase text-slate-500 tracking-wider">{(u.payment_method || "PayPal")} email</div>
                <div className="font-medium text-slate-900 mt-0.5">{u.payment_id || <span className="text-sm text-slate-500 italic">Not set</span>}</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3" data-testid="payout-edit">
              <div>
                <Label>Payout method</Label>
                <Select value={draft.payment_method || "PayPal"} onValueChange={v=>setDraft(d=>({...d, payment_method: v, payment_id: ""}))}>
                  <SelectTrigger data-testid="select-payout-method"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PayPal">PayPal</SelectItem>
                    <SelectItem value="Payoneer">Payoneer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{(draft.payment_method || "PayPal")} email</Label>
                <Input
                  type="email"
                  value={draft.payment_id || ""}
                  onChange={e=>setDraft(d=>({...d, payment_id: e.target.value}))}
                  placeholder={(draft.payment_method || "PayPal") === "PayPal" ? "name@paypal.com" : "name@payoneer.com"}
                  autoCapitalize="none"
                  autoCorrect="off"
                  inputMode="email"
                  data-testid="input-payout-email"
                />
              </div>
            </div>
          )}
        </div>

        {/* Statement downloader */}
        <div className="pt-4 border-t border-slate-100">
          <div className="text-[11px] uppercase text-slate-500 tracking-wider mb-2">Earnings statement</div>
          <p className="text-xs text-slate-500 mb-3">Download approved + paid transactions for a date range as CSV or PDF.</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label className="text-xs">From</Label>
              <Input type="date" value={start} max={today} onChange={e=>setStart(e.target.value)} data-testid="input-statement-start"/>
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input type="date" value={end} max={today} onChange={e=>setEnd(e.target.value)} data-testid="input-statement-end"/>
            </div>
            <Button variant="outline" onClick={()=>download("csv")} disabled={downloading!==null} data-testid="btn-download-csv">
              <Download className="w-3.5 h-3.5 mr-1.5"/>{downloading==="csv" ? "Preparing…" : "Download CSV"}
            </Button>
            <Button onClick={()=>download("pdf")} disabled={downloading!==null} className="bg-slate-900 hover:bg-slate-800" data-testid="btn-download-pdf">
              <Download className="w-3.5 h-3.5 mr-1.5"/>{downloading==="pdf" ? "Preparing…" : "Download PDF"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
